import { env } from "@/env";
import {
  deleteCookie,
  getCookie,
  type OptionsType,
  setCookie,
} from "cookies-next/client";
import { create } from "zustand";
import {
  createJSONStorage,
  persist,
  type StateStorage,
} from "zustand/middleware";
import {
  LANGUAGE_COOKIE_NAME,
  MARKET_COOKIE_NAME,
  SUGGESTION_COOKIE_NAME,
} from "../config/cookies";
import { Market } from "../config/i18n-config";
import { findMarketByCode, markets } from "../config/market-config";

const cookieStorage = (options?: OptionsType): StateStorage => ({
  getItem: () => {
    const code = getCookie(MARKET_COOKIE_NAME);
    const suggested = getCookie(SUGGESTION_COOKIE_NAME);

    if (env.NEXT_PUBLIC_APP_MARKET === "no") {
      setCookie(MARKET_COOKIE_NAME, markets.no.countryCode, options);
      setCookie(LANGUAGE_COOKIE_NAME, markets.no.languageCode, options);
      return JSON.stringify({
        state: { market: markets.no.countryCode, suggestedMarket: undefined },
        version: 0,
      });
    }

    if (env.NEXT_PUBLIC_APP_MARKET === "se") {
      setCookie(MARKET_COOKIE_NAME, markets.se.countryCode, options);
      setCookie(LANGUAGE_COOKIE_NAME, markets.se.languageCode, options);
      return JSON.stringify({
        state: { market: markets.se.countryCode, suggestedMarket: undefined },
        version: 0,
      });
    }

    if (!code) {
      return null;
    }

    const market = findMarketByCode(code.valueOf());
    const suggestedMarket = suggested
      ? findMarketByCode(suggested.valueOf())
      : undefined;

    return JSON.stringify({
      state: { market, suggestedMarket },
      version: 0,
    });
  },
  setItem: async (_, value: string) => {
    const market = findMarketByCode(value);
    setCookie(MARKET_COOKIE_NAME, market.countryCode, options);
    setCookie(LANGUAGE_COOKIE_NAME, market.languageCode, options);
    deleteCookie(SUGGESTION_COOKIE_NAME);
  },
  removeItem: async () => {
    deleteCookie(MARKET_COOKIE_NAME);
    deleteCookie(LANGUAGE_COOKIE_NAME);
    deleteCookie(SUGGESTION_COOKIE_NAME);
  },
});

type MarketStore = {
  open: boolean;
  setOpen: (open: boolean) => void;
  market?: string;
  setMarket: (market: Market) => void;
  suggestedMarket?: string;
  setSuggestedMarket: (suggestedMarket: Market) => void;
};

export const useMarketStore = create<MarketStore>()(
  persist(
    (set) => ({
      open: false,
      setOpen: (open: boolean) => set({ open }),
      market: undefined,
      setMarket: (market: Market) =>
        set({ market: market.countryCode, open: false }),
      suggestedMarket: undefined,
      setSuggestedMarket: (suggestedMarket: Market) =>
        set({ suggestedMarket: suggestedMarket.countryCode }),
    }),
    {
      name: MARKET_COOKIE_NAME,
      storage: createJSONStorage(() =>
        cookieStorage({
          sameSite: "strict",
          maxAge: 60 * 60 * 24 * 365,
        }),
      ),
      partialize: (state) => ({
        market: state.market,
        suggestedMarket: state.suggestedMarket,
      }),
    },
  ),
);
